
  import { upload, download } from '@/services/api/resources/entityItem';
  import emitter from '@/services/emitter';
  import { Button } from '@/ui/components/Button';
  import DragAndDropUpload from '@/ui/components/common/DragAndDropUpload.vue';
  import { Modal } from '@/ui/components/Modal';
  import { ENTITIES_UPLOAD_MODAL } from '@/ui/components/modals/constants';
  import Spinner from '@/ui/components/Spinner.vue';
  import { useTask } from '@/ui/concerns';
  import { useValidationErrors } from '@/ui/concerns/entityItems/useValidationErrors';
  import { toasted } from '@/ui/helpers';
  import { t } from '@/ui/i18n/i18n';
  import { computed, defineComponent, ref } from 'vue';
  import { v4 as uuidv4 } from 'uuid';
  import { saveFileFromResponse } from '@/ui/helpers/fileHelper';
  import { StatusCodes } from 'http-status-codes';

  interface EntitiesUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    forType?: number;
  }

  export default defineComponent<EntitiesUploadModalProps>({
    components: {
      DragAndDropUpload,
      Spinner,
      Modal,
      Button,
    },
    props: {
      isOpen: {
        type: Boolean,
        required: true,
      },
      onClose: {
        type: Function,
        required: true,
      },
      forType: {
        type: Number,
        required: false,
      },
    },
    setup(props) {
      const loading = ref(false);
      const exporting = ref(false);
      const uploadedFile = ref<File|null>(null);
      const formats = ['.XLSX'];
      const types = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

      const isSubmitDisabled = computed(() => loading.value || !uploadedFile.value);
      const { displayValidationErrors } = useValidationErrors();

      const onImport = async () => {
        loading.value = true;
        await uploadTask.run();
      };

      const onExport = async () => {
        await exportTask.run();
      };

      const doClose = () => {
        props.onClose();
        loading.value = false;
      };

      const uploadTask = useTask(
        () => {
          loading.value = true;

          return upload(uploadedFile.value!, props.forType ? { forType: props.forType } : undefined);
        },
        () => {
          toasted.success(t('entity-item.upload-modal.success'));
          emitter.emit('reloadEntityItems');
          doClose();
        },
        uuidv4(),
        e => {
          loading.value = false;
          if (e.messages && e.messages.length > 0) {
            displayValidationErrors(e);
          } else {
            const errorMessage = e?.message || t('entity-item.upload-modal.error.upload');
            toasted.error(errorMessage);
          }
        },
      );

      const exportTask = useTask(
        () => {
          exporting.value = true;

          return download(props.forType ? { forType: props.forType } : undefined);
        },
        async (response: Response) => {
          try {
            exporting.value = false;

            if (response.status !== StatusCodes.OK) {
              toasted.error(t('entity-item.upload-modal.error.export'));
              return;
            }

            await saveFileFromResponse(response, 'entities.xls').catch(e => {
              console.error(e);
              toasted.error(t('entity-item.upload-modal.error.export'));
            });
          } catch (e) {
            console.error(e);
            exporting.value = false;
            toasted.error(t('entity-item.upload-modal.error.export'));
          }
        },
        uuidv4(),
        e => {
          console.error(e);
          exporting.value = false;
          const errorMessage = e?.message || t('entity-item.upload-modal.error.export');
          toasted.error(errorMessage);
        },
      );

      const onFileUploadChange = (data: File[]) => {
        if (!data.length) {
          return;
        }

        const [file] = data;
        uploadedFile.value = file;
      };

      const clearPreview = () => {
        uploadedFile.value = null;
      };

      return {
        onImport,
        onExport,
        loading,
        exporting,
        ENTITIES_UPLOAD_MODAL,
        doClose,
        formats,
        types,
        onFileUploadChange,
        clearPreview,
        isSubmitDisabled,
        entityType: props.forType,
      };
    },
  });
